import type { WallInterval } from "./wallTraverseTypes";

// Function to calculate the width of a wall interval
export function getIntervalWidth(interval: WallInterval) {
  const {
    right: { param: rParam },
    left: { param: lParam },
  } = interval;
  const intervalWidth = lParam - rParam;
  return intervalWidth;
}

/**
 * Function to subdivide a wall interval with another interval. The entities associated with each interval
 * are combined and can be used to start traverses from the right or left side of the associated entity.
 * The interval to subdivide with is assumed to be within the bounds of the parent interval. Any interval
 * beyond the bounds of the parent interval will be ignored.
 *
 * @param intervals The intervals to subdivide
 * @param interval  The interval to subdivide with
 * @param snapThreshold Distance to either side of a parent interval to snap a child interval start or end to
 */
export function subdivideWallIntervalsWithInterval(
  intervals: WallInterval[],
  interval: WallInterval,
  snapThreshold: number = 0.5,
) {
  const newIntervals: WallInterval[] = [];

  /**
   *  If a start or end extent has been placed, don't place another. There are situations where
   * a start or end will snap to multiple parent intervals, and we only want to place it once.
   * */
  let sExtentsPlaced = false;
  let eExtentsPlaced = false;

  for (const parentInterval of intervals) {
    const ps = parentInterval.right;
    const pe = parentInterval.left;

    const s = interval.right;
    const e = interval.left;

    /**
     * if the interval is completely outside the parent interval, defined as start within or greater
     * than the parent end snap or end within or less than the parent start snap, then don't subdivide
     **/
    if (s.param > pe.param - snapThreshold || e.param < ps.param + snapThreshold) {
      newIntervals.push(parentInterval);
      continue;
    }

    const sLessThanOrEqualToPs = s.param < ps.param + snapThreshold;
    const eGreaterThanOrEqualToPe = e.param > pe.param - snapThreshold;
    const sSnapsToPs = !sExtentsPlaced && Math.abs(s.param - ps.param) < snapThreshold;
    const eSnapsToPe = !eExtentsPlaced && Math.abs(e.param - pe.param) < snapThreshold;

    if (sLessThanOrEqualToPs && eGreaterThanOrEqualToPe) {
      // keep original interval but combine descriptors and extent ids
      newIntervals.push({
        right: {
          param: ps.param,
          extentIds: sSnapsToPs ? { ...ps.extentIds, ...s.extentIds } : { ...ps.extentIds },
        },
        left: {
          param: pe.param,
          extentIds: eSnapsToPe ? { ...pe.extentIds, ...e.extentIds } : { ...pe.extentIds },
        },
        entities: !eExtentsPlaced
          ? { ...parentInterval.entities, ...interval.entities }
          : { ...parentInterval.entities },
        frontEntityIds: !eExtentsPlaced
          ? { ...parentInterval.frontEntityIds, ...interval.frontEntityIds }
          : { ...parentInterval.frontEntityIds },
        backEntityIds: !eExtentsPlaced
          ? { ...parentInterval.backEntityIds, ...interval.backEntityIds }
          : { ...parentInterval.backEntityIds },
      });

      if (sSnapsToPs) {
        sExtentsPlaced = true;
      }
      if (eSnapsToPe) {
        eExtentsPlaced = true;
      }
      continue;
    }

    if (!sLessThanOrEqualToPs && !eGreaterThanOrEqualToPe) {
      // all new intervals with no combination of extent ids
      newIntervals.push({
        right: ps,
        left: { param: s.param, extentIds: {} },
        entities: { ...parentInterval.entities },
        frontEntityIds: { ...parentInterval.frontEntityIds },
        backEntityIds: { ...parentInterval.backEntityIds },
      });
      newIntervals.push({
        right: s,
        left: e,
        entities: { ...parentInterval.entities, ...interval.entities },
        frontEntityIds: { ...parentInterval.frontEntityIds, ...interval.frontEntityIds },
        backEntityIds: { ...parentInterval.backEntityIds, ...interval.backEntityIds },
      });
      newIntervals.push({
        right: { param: e.param, extentIds: {} },
        left: pe,
        entities: { ...parentInterval.entities },
        frontEntityIds: { ...parentInterval.frontEntityIds },
        backEntityIds: { ...parentInterval.backEntityIds },
      });

      sExtentsPlaced = true;
      eExtentsPlaced = true;

      continue;
    }

    if (!sLessThanOrEqualToPs) {
      // add interval before new one
      newIntervals.push({
        right: ps,
        left: { param: s.param, extentIds: {} },
        entities: { ...parentInterval.entities },
        frontEntityIds: { ...parentInterval.frontEntityIds },
        backEntityIds: { ...parentInterval.backEntityIds },
      });

      // combine ends
      newIntervals.push({
        right: s,
        left: {
          param: pe.param,
          extentIds: eSnapsToPe ? { ...pe.extentIds, ...e.extentIds } : { ...pe.extentIds },
        },
        entities: !eExtentsPlaced
          ? { ...parentInterval.entities, ...interval.entities }
          : { ...parentInterval.entities },
        frontEntityIds: !eExtentsPlaced
          ? { ...parentInterval.frontEntityIds, ...interval.frontEntityIds }
          : { ...parentInterval.frontEntityIds },
        backEntityIds: !eExtentsPlaced
          ? { ...parentInterval.backEntityIds, ...interval.backEntityIds }
          : { ...parentInterval.backEntityIds },
      });
      if (eSnapsToPe) {
        eExtentsPlaced = true;
      }
      continue;
    }

    // !eGreaterThanOrEqualToPe is assumed, so
    // combine starts
    newIntervals.push({
      right: {
        param: ps.param,
        extentIds: sSnapsToPs ? { ...ps.extentIds, ...s.extentIds } : { ...ps.extentIds },
      },
      left: e,
      entities: { ...parentInterval.entities, ...interval.entities },
      frontEntityIds: { ...parentInterval.frontEntityIds, ...interval.frontEntityIds },
      backEntityIds: { ...parentInterval.backEntityIds, ...interval.backEntityIds },
    });
    newIntervals.push({
      right: { param: e.param, extentIds: {} },
      left: pe,
      entities: { ...parentInterval.entities },
      frontEntityIds: { ...parentInterval.frontEntityIds },
      backEntityIds: { ...parentInterval.backEntityIds },
    });

    if (sSnapsToPs) {
      sExtentsPlaced = true;
    }
  }

  return newIntervals;
}
